import { useMemo, useState, type JSX } from 'react';
import { EnvelopeClosedIcon, InfoCircledIcon, MobileIcon } from '@radix-ui/react-icons';
import {
  EntityStatWidget,
  EntityTable,
  PillButton,
  type EntityTableColumn,
} from '@/components/entity';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { Drawer } from '@/components/layout/Drawer';
import { TenantProfilePreview } from './TenantProfilePreview';
import { WebsiteLink } from './WebsiteLink';
import {
  EMPTY_VALUE,
  TENANT_PROFILES,
  categoryLabel,
  orDash,
  type TenantProfile,
} from './tenantProfileData';

export function TenantProfilesPanel(): JSX.Element {
  const [activeTenant, setActiveTenant] = useState<TenantProfile | null>(null);

  const stats = useMemo(() => {
    const total = TENANT_PROFILES.length;
    const featured = TENANT_PROFILES.filter((tenant) => tenant.featured).length;
    const categories = new Set(TENANT_PROFILES.map((tenant) => tenant.categoryId)).size;
    return { total, featured, categories };
  }, []);

  // The contact column only appears once real contact data lands on the roster,
  // so an empty column never shows while those fields are still managed in code.
  const hasContactData = useMemo(
    () =>
      TENANT_PROFILES.some(
        (tenant) =>
          (tenant.email != null && tenant.email.trim().length > 0) ||
          (tenant.phone != null && tenant.phone.trim().length > 0),
      ),
    [],
  );

  const columns = useMemo<EntityTableColumn<TenantProfile>[]>(() => {
    const definitions: EntityTableColumn<TenantProfile>[] = [
      {
        id: 'name',
        header: 'Tenant',
        renderCell: (tenant) => (
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-text-primary">{tenant.name}</span>
            <span className="text-xs text-text-primary/60">{tenant.tagline}</span>
            {tenant.featured ? <StatusBadge label="Featured" tone="info" className="mt-1" /> : null}
          </div>
        ),
      },
      {
        id: 'category',
        header: 'Category',
        renderCell: (tenant) => (
          <span className="text-sm text-text-primary/80">{categoryLabel(tenant.categoryId)}</span>
        ),
      },
      {
        id: 'location',
        header: 'Suite / Floor',
        renderCell: (tenant) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-text-primary">{orDash(tenant.suite)}</span>
            <span className="text-xs text-text-primary/60">{tenant.floor}</span>
          </div>
        ),
      },
      {
        id: 'hours',
        header: 'Hours',
        renderCell: (tenant) => (
          <div className="flex flex-col gap-1">
            <span className="text-sm text-text-primary/80">{orDash(tenant.hours)}</span>
            {tenant.appointmentOnly ? (
              <span className="text-xs uppercase tracking-wide text-text-primary/50">
                By appointment
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'website',
        header: 'Website',
        renderCell: (tenant) => <WebsiteLink website={tenant.website} />,
      },
    ];

    if (hasContactData) {
      definitions.push({
        id: 'contact',
        header: 'Contact',
        renderCell: (tenant) => {
          const email = tenant.email?.trim();
          const phone = tenant.phone?.trim();
          const hasEmail = email != null && email.length > 0;
          const hasPhone = phone != null && phone.length > 0;
          if (!hasEmail && !hasPhone) {
            return <span className="text-text-primary/40">{EMPTY_VALUE}</span>;
          }
          return (
            <div className="flex flex-col gap-1 text-sm">
              {hasEmail ? (
                <a
                  href={`mailto:${email}`}
                  className="inline-flex items-center gap-1 text-brand-primary hover:underline"
                >
                  <EnvelopeClosedIcon className="h-3.5 w-3.5" aria-hidden />
                  {email}
                </a>
              ) : null}
              {hasPhone ? (
                <a
                  href={`tel:${phone}`}
                  className="inline-flex items-center gap-1 text-text-primary/80 hover:underline"
                >
                  <MobileIcon className="h-3.5 w-3.5" aria-hidden />
                  {phone}
                </a>
              ) : null}
            </div>
          );
        },
      });
    }

    definitions.push({
      id: 'actions',
      header: <span className="sr-only">Preview</span>,
      align: 'right',
      renderCell: (tenant) => (
        <PillButton
          variant="secondary"
          size="sm"
          onClick={() => {
            setActiveTenant(tenant);
          }}
        >
          Preview
        </PillButton>
      ),
    });

    return definitions;
  }, [hasContactData]);

  return (
    <div className="flex flex-col gap-xl">
      <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <InfoCircledIcon className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden />
        <p>
          <span className="font-semibold">Read-only preview</span> — inline editing is coming in a
          future update. Tenant profiles are currently managed in code.
        </p>
      </div>

      <div className="grid gap-md sm:grid-cols-3">
        <EntityStatWidget
          label="Total tenants"
          value={stats.total}
          description="501 Alliance roster"
        />
        <EntityStatWidget
          label="Featured"
          value={stats.featured}
          description="Highlighted on the microsite"
          tone="success"
        />
        <EntityStatWidget
          label="Categories"
          value={stats.categories}
          description="Across the collective"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-sm">
        <EntityTable
          items={TENANT_PROFILES}
          columns={columns}
          getRowKey={(tenant) => tenant.slug}
          onRowClick={(tenant) => {
            setActiveTenant(tenant);
          }}
          emptyState={
            <div className="rounded-2xl border border-dashed border-surface-subtle bg-white/60 p-6 text-center text-sm text-text-primary/70">
              No tenant profiles are available yet.
            </div>
          }
        />
      </div>

      <Drawer
        open={activeTenant !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActiveTenant(null);
          }
        }}
        title={activeTenant?.name}
        description={activeTenant?.tagline}
        size="lg"
      >
        {activeTenant != null ? <TenantProfilePreview tenant={activeTenant} /> : null}
      </Drawer>
    </div>
  );
}
